/**
 * CMS server functions. Thin wrappers only — runtime logic lives in
 * `cms.server.ts` so server-function splitting cannot drop it.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CmsBlock, CmsSeo, CmsStatus } from "@/lib/admin/cms/store";

type SaveInput = {
  slug: string;
  title: string;
  description: string;
  seo: CmsSeo;
  blocks: CmsBlock[];
  status?: CmsStatus;
};

export const getPublishedCmsPageFn = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug) throw new Error("slug required");
    return input;
  })
  .handler(async ({ data }) => {
    const s = await import("./cms.server");
    return s.fetchPublishedPage(data.slug);
  });

export const getCmsPageFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug) throw new Error("slug required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./cms.server");
    return s.fetchPage(data.slug);
  });

export const listCmsPagesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./cms.server");
    return s.fetchPages();
  });

export const saveCmsPageFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: SaveInput) => {
    if (!input?.slug) throw new Error("slug required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./cms.server");
    return s.savePage(data, context.userId);
  });

export const setCmsPageStatusFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string; status: CmsStatus }) => {
    if (!input?.slug || !input?.status) throw new Error("slug and status required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./cms.server");
    return s.setPageStatus(data.slug, data.status, context.userId);
  });
